/**
 * The tenant-wide "which sessions are running" scan — ONE shared poll.
 *
 * `GET /api/agent/sessions/status` answers a tenant-wide question, but it used
 * to be polled by three independent hand-rolled `setInterval`s
 * (`useAgentSession`'s cross-tab sync at 4s, `useSessionActivity` at 5s, the
 * chat-home rail at 5s). Each ran per *component instance*, so mounting two
 * chat surfaces doubled the traffic, and none of them stopped when the tab was
 * hidden or when nothing was running. A single idle tab sustained roughly one
 * request per second, and each request makes the server fetch and deserialize
 * every active+waiting BullMQ job across ALL tenants before filtering to the
 * caller's.
 *
 * Consolidating on one TanStack query key fixes that structurally:
 *
 *  - **Dedup.** Every consumer shares `busySessionsKey`, so N mounted
 *    components produce ONE request stream, not N.
 *  - **Hidden tabs stop.** `refetchIntervalInBackground` defaults to false,
 *    which a raw `setInterval` never respected.
 *  - **Idle backs off.** See the interval note below.
 *
 * This is a fallback path, not the primary one: session lifecycle already
 * streams over SSE (`/api/agent/event` → `session.updated`). The poll exists to
 * recover events the stream drops, which is why backing it off while idle is
 * safe — see the comment on IDLE_POLL_MS.
 */
import { useQuery } from '@tanstack/react-query'
import { request } from '@papan/api-client'

export interface BusyRow {
  id: string
  workspaceId: string
  busy: true
  /** What is keeping it busy. A `'compaction'` is NOT a conversational run —
   *  it cannot be steered into and cannot be stopped, so a consumer must not
   *  attach an AG-UI run stream to it or the composer will invite input that
   *  is silently destroyed.
   *
   *  Optional and ABSENT ⇒ treat as `'prompt'`: an older API during a rolling
   *  restart omits it, and the pre-existing behaviour was to attach. */
  kind?: 'prompt' | 'compaction'
}

/** Shared across every consumer — that sharing IS the dedup. */
export const busySessionsKey = ['agent', 'busy-sessions'] as const

/**
 * While something is running we're watching for it to FINISH, which drives the
 * done-dot and the end of the live stream. Keep that tight.
 */
const BUSY_POLL_MS = 4_000

/**
 * While nothing is running the only event we could miss is a run STARTING
 * somewhere else, and SSE already delivers that instantly. This poll is purely
 * the backstop for a dropped event, so a slower beat costs a little latency on
 * an already-redundant path and saves ~4x the requests on an idle tab. Note the
 * busy→idle transition is observed at the FAST rate: the set is still non-empty
 * on the tick that sees the run end.
 */
const IDLE_POLL_MS = 15_000

/**
 * Subscribe to the tenant's busy-session set.
 *
 * Every caller gets the same cached array and the same underlying poll. Safe to
 * call from as many components as you like.
 */
export function useBusySessions() {
  return useQuery({
    queryKey: busySessionsKey,
    queryFn: () => request<BusyRow[]>('/api/agent/sessions/status'),
    // Consumers diff this set against the previous one to detect completions,
    // so they must never be handed a stale-but-fresh-looking cache entry.
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data && query.state.data.length > 0 ? BUSY_POLL_MS : IDLE_POLL_MS,
  })
}
